import { createMaperLightTheme, type CustomTokens } from '../MaperTheme';

// Single source of truth for health colour. Each state is a trio — background, border and
// text — verified for AA contrast, mapped onto the token structure as:
//   *1Bg     = border/icon tone (consumed as `fg`: card side-stripe, icon plate, marks)
//   *1Stroke = same border tone, for borders on tinted surfaces
//   *2Bg     = background tint  (consumed as `bg`)
//   *2Fg     = text tone        (consumed as `text`: label on a tinted surface)
//
// `*1Bg` and `*2Fg` are split because the side-stripe and icon need the vivid border tone,
// while a chip label needs the darker one to clear AA against the tint.
const customTokensLight: CustomTokens = {
    // Health status - Primary variants (border/icon tone — the vivid step)
    colorHealthOptimal1Bg: '#0F7A3A',
    colorHealthOptimal1Fg: '#E1F5EE',
    colorHealthOptimal1Stroke: '#4FB77C',

    colorHealthAcceptable1Bg: '#C9B84A',
    colorHealthAcceptable1Fg: '#F5F3DC',
    colorHealthAcceptable1Stroke: '#C9B84A',

    colorHealthAttention1Bg: '#F18A3A',
    colorHealthAttention1Fg: '#FFF1E3',
    colorHealthAttention1Stroke: '#F18A3A',

    colorHealthDanger1Bg: '#DC2626',
    colorHealthDanger1Fg: '#FEF2F2',
    colorHealthDanger1Stroke: '#DC2626',

    colorHealthUnknown1Bg: '#ADB5BD',
    colorHealthUnknown1Fg: '#F3F4F6',
    colorHealthUnknown1Stroke: '#ADB5BD',

    // Health status - Secondary variants (background tint + the text tone that sits on it)
    colorHealthOptimal2Bg: '#E1F5EE',
    colorHealthOptimal2Fg: '#0F7A3A', // 5.0:1 AA on the tint
    colorHealthOptimal2Stroke: '#4FB77C',

    colorHealthAcceptable2Bg: '#F5F3DC',
    colorHealthAcceptable2Fg: '#7F7200', // 4.4:1 on the tint — AA for large text only
    colorHealthAcceptable2Stroke: '#C9B84A',

    colorHealthAttention2Bg: '#FFF1E3',
    colorHealthAttention2Fg: '#C67336', // 3.2:1 on the tint — AA for large text only
    colorHealthAttention2Stroke: '#F18A3A',

    colorHealthDanger2Bg: '#FEF2F2',
    colorHealthDanger2Fg: '#991B1B', // 7.6:1 AAA
    colorHealthDanger2Stroke: '#DC2626',

    colorHealthUnknown2Bg: '#F3F4F6',
    colorHealthUnknown2Fg: '#6B7280', // 4.8:1 AA
    colorHealthUnknown2Stroke: '#ADB5BD',

    // Informative / in-progress blue. #185FA5 on #EAF2FB is 6.5:1 → AA.
    //
    // NOT Fluent's badge "Informative" preset, which resolves to `colorNeutralForeground3` on
    // `colorNeutralBackground5` — grey, not blue. That preset backs the NEUTRAL chips.
    colorInfo1Bg: '#185FA5',
    colorInfo1Stroke: '#185FA5',
    colorInfo2Bg: '#EAF2FB',
    colorInfo2Fg: '#185FA5',

    // Event accents (light). These `Fg` values also render as text (the Win card's saved
    // capital, the alarm count), so they take the darker step, not the vivid one.
    colorEventWinFg: '#0F7A3A',
    colorEventWinBg: '#E1F5EE',
    colorEventWinStroke: '#4FB77C',

    colorEventAlarmFg: '#DC2626',
    colorEventAlarmBg: '#FEF2F2',
    colorEventAlarmStroke: '#DC2626',
};

export const lightTheme = createMaperLightTheme(customTokensLight);
